import { Fragment, useEffect, useMemo, useState } from "react";
import { useGlobalFunction } from "../../Context/GlobalFunctionContext";
import { isValidArray } from "../../Layout/Common/common";
import { fetchRelatesProducts } from "../../Layout/Common/algoliaInstantLib";
import { ProductCard } from "../../Layout/Common/productCard";
import { ProductCardAlgolia } from "../../Layout/Common/productCardAlgolia";
import { ProductCarouselSection } from "../../Layout/Common/productCarouselSection";

const MAX_CAROUSEL_ITEMS = 12;

const toProductList = (value) => {
  if (isValidArray(value)) return value;
  if (typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value);
    return isValidArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const renderEntry = (entry) =>
  entry.source === "db" ? <ProductCard data={entry.row} /> : <ProductCardAlgolia data={entry.row} />;

const buildEntries = (rows, source) => {
  const seen = new Set();
  const out = [];
  (rows ?? []).forEach((row, index) => {
    if (!row) return;
    const id = String(row.productId ?? "");
    if (id) {
      if (seen.has(id)) return;
      seen.add(id);
    }
    out.push({ key: `${source}-${id || index}`, source, row });
  });
  return out.slice(0, MAX_CAROUSEL_ITEMS);
};

export const RelatedProductsContainer = ({ params }) => {
  const [algoliaItems, setAlgoliaItems] = useState(null);
  const [suggestionItems, setSuggestionItems] = useState(null);
  const { getSuggestionProducts } = useGlobalFunction();

  useEffect(() => {
    setAlgoliaItems(null);
    setSuggestionItems(null);

    const details = params?.familyDetails;
    if (!details || details.isFamily != false) return;

    const controller = new AbortController();
    if (details.productId) loadAlgolia(details.productId, controller.signal);
    loadSuggestions(details.familyId, details.productId, controller.signal);

    return () => controller.abort();
  }, [params]);

  const isAborted = (err, signal) => signal?.aborted || err?.code === "ERR_CANCELED" || err?.name === "CanceledError";

  const loadAlgolia = async (productId, signal) => {
    try {
      const items = await fetchRelatesProducts(productId);
      if (signal?.aborted) return;
      setAlgoliaItems(isValidArray(items) ? items : null);
    } catch (err) {
      if (isAborted(err, signal)) return;
      console.error("RelatedProductsContainer algolia error", err);
      setAlgoliaItems(null);
    }
  };

  const loadSuggestions = async (familyId, productId, signal) => {
    try {
      const results = toProductList(await getSuggestionProducts(familyId, productId, signal));
      if (signal?.aborted) return;
      if (!isValidArray(results)) {
        setSuggestionItems(null);
        return;
      }
      const filtered = productId ? results.filter((a) => parseInt(a.productId) != parseInt(productId)) : results;
      setSuggestionItems(filtered);
    } catch (err) {
      if (isAborted(err, signal)) return;
      console.error("RelatedProductsContainer suggestions error", err);
      setSuggestionItems(null);
    }
  };

  const suggestionEntries = useMemo(() => buildEntries(suggestionItems, "db"), [suggestionItems]);
  const relatedEntries = useMemo(() => buildEntries(algoliaItems, "algolia"), [algoliaItems]);

  return (
    <Fragment>
      <ProductCarouselSection
        title="Suggested Products"
        sectionClassName="proSection suggestionProduct-section"
        items={suggestionEntries}
        getItemKey={(entry) => entry.key}
        renderItem={renderEntry}
      />
      <ProductCarouselSection
        title="Related Products"
        sectionClassName="proSection relatedProduct-section"
        items={relatedEntries}
        getItemKey={(entry) => entry.key}
        renderItem={renderEntry}
      />
    </Fragment>
  );
};
